import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useEffect,
  useState,
} from "react";

import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import ButtonWithConfirm from "components/utils/ButtonWithConfirm";
import {
  Button,
  DialogActions,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
} from "@mui/material";

import conf from "config-dlm";
import { User } from "components/auth/AuthProvider";
import {
  useUserServiceUpdateAccess,
  useUserServiceUpdateRole,
} from "hooks/user/base";

export type ActionType = "assign" | "unassign";

type ToggleGroupProps = { user: User };

export const RoleToggleGroup = (props: ToggleGroupProps) => {
  const { id, email, role: current } = props.user;

  const [role, setRole] = useState(current);
  const [editing, setEditing] = useState(false);
  const [isAdmin, setIsAdmin] = useState(role === "admin");

  const update = useUserServiceUpdateRole();

  const warning = (
    <>
      You are about to update <b>{email}</b>'s role from <b>{current}</b> to{" "}
      <b>{role}</b>.
      <br />
      This action cannot be undone. Are you sure you want to continue?
    </>
  );

  const handleCancel = () => {
    setRole(current);
    setEditing(!editing);
  };

  const handleConfirm = () => update.mutate({ id, role });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRole((e.target as HTMLInputElement).value as User["role"]);
  };

  useEffect(() => {
    if (update.isSuccess) setEditing(false);
  }, [update.isSuccess]);

  return (
    <Stack>
      <FormControl>
        <RadioGroup
          row
          name="role-toggle-group"
          value={role}
          onChange={handleChange}
          aria-labelledby="role-toggle-group-label"
        >
          {conf.ROLES.map((role) => (
            <FormControlLabel
              key={role}
              value={role}
              label={role}
              control={<Radio />}
              disabled={isAdmin || !editing || role === "admin"}
            />
          ))}
        </RadioGroup>
      </FormControl>

      <DialogActions>
        <Button variant="outlined" disabled={isAdmin} onClick={handleCancel}>
          {editing ? "Cancel" : "Edit"}
        </Button>

        <ButtonWithConfirm
          type="Role Update"
          label="Submit"
          color="primary"
          message={warning}
          disabled={!editing}
          onConfirm={handleConfirm}
        />
      </DialogActions>
    </Stack>
  );
};

type ActionToggleGroupProps = {
  action: ActionType;
  setAction: Dispatch<SetStateAction<ActionType>>;
};

export const ActionToggleGroup = (props: ActionToggleGroupProps) => {
  const { action, setAction } = props;

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setAction(e.target.value as ActionType);
  };

  return (
    <RadioGroup
      row
      value={action}
      onChange={handleChange}
      aria-label="Admin action type"
    >
      <FormControlLabel value="assign" label="assign" control={<Radio />} />
      <FormControlLabel value="unassign" label="unassign" control={<Radio />} />
    </RadioGroup>
  );
};

export const AccessToggleGroup = (props: ToggleGroupProps) => {
  const user = props.user;
  const { id, email } = user;

  const [status, setStatus] = useState(props.user.status);
  const [editing, setEditing] = useState(false);

  const update = useUserServiceUpdateAccess();

  const warning = (
    <>
      You are about to change <b>{email}</b>'s account access to{" "}
      <b>{status.toUpperCase()}</b>.
      <br />
      This action cannot be undone. Are you sure you want to continue?
    </>
  );

  const handleCancel = () => {
    setStatus(user.status);
    setEditing(!editing);
  };

  const handleConfirm = () => update.mutate({ id, status });

  const handleChange = (e: SelectChangeEvent) => {
    setStatus(e.target.value as User["status"]);
  };

  useEffect(() => {
    if (update.isSuccess) setEditing(false);
  }, [update.isSuccess]);

  return (
    <DialogActions sx={{ pl: 0 }}>
      <FormControl fullWidth>
        <Select
          id="status-select"
          labelId="status-select-label"
          size="small"
          value={status}
          onChange={handleChange}
        >
          <MenuItem value={"active"}>Active</MenuItem>
          <MenuItem value={"pending"} disabled>
            Pending
          </MenuItem>
          <MenuItem value={"suspended"}>Suspended</MenuItem>
          <MenuItem value={"banned"}>Banned</MenuItem>
          <MenuItem value={"deleted"}>Deleted</MenuItem>
        </Select>
      </FormControl>

      <Button
        variant="outlined"
        onClick={handleCancel}
        disabled={user.status === status}
      >
        Reset
      </Button>

      <ButtonWithConfirm
        type="Update access"
        label="Submit"
        color="primary"
        message={warning}
        disabled={user.status === status}
        onConfirm={handleConfirm}
      />
    </DialogActions>
  );
};
